import { useRef, useCallback } from 'react';
import { supabase } from '../components/Data/DataProvider';
import { normalizeExpert, shuffleWithSeed } from '../utils/expertUtils';

/**
 * Hook to handle expert data fetching and caching
 * Manages: Supabase queries, VIP fetching, tags/media caching, metadata backfilling
 */
export function useExpertData() {
  const tagsCacheRef = useRef(new Map());
  const mediaCacheRef = useRef(new Map());
  const ID_ALIASES = useRef(new Map());

  const fetchExpertsByIdsInOrder = useCallback(async (ids, { markVip = false } = {}) => {
    const resolvedIds = (ids || []).map((id) => ID_ALIASES.current.get(id) ?? id);
    if (!resolvedIds.length) return [];

    const { data, error } = await supabase
      .from('experts')
      .select('*')
      .in('id', resolvedIds);

    if (error) throw error;

    const byId = new Map((data || []).map(e => [Number(e.id), e]));
    return resolvedIds
      .map(id => byId.get(id))
      .filter(Boolean)
      .map(e => normalizeExpert(e, { isVip: markVip }));
  }, []);

  const fetchPriorityExperts = useCallback(async (vipIds) => {
    return fetchExpertsByIdsInOrder(vipIds, { markVip: true });
  }, [fetchExpertsByIdsInOrder]);

  const fetchTagsForIds = useCallback(async (ids) => {
    const out = {};
    const missing = [];

    for (const id of ids) {
      if (tagsCacheRef.current.has(id)) {
        out[id] = tagsCacheRef.current.get(id);
      } else {
        missing.push(id);
      }
    }

    if (missing.length) {
      await Promise.all(
        missing.map(async (id) => {
          const { data, error } = await supabase
            .from('tags')
            .select('*')
            .eq('tagged_id', id);
          const val = error ? [] : (data ?? []);
          tagsCacheRef.current.set(id, val);
          out[id] = val;
        })
      );
    }

    return out;
  }, []);

  const fetchMediaCounts = useCallback(async (ids = []) => {
    const out = {};
    const missing = [];

    for (const id of ids) {
      if (mediaCacheRef.current.has(id)) {
        out[id] = mediaCacheRef.current.get(id);
      } else {
        missing.push(id);
      }
    }

    if (missing.length) {
      await Promise.all(
        missing.map(async (id) => {
          const { count, error } = await supabase
            .from('media_items')
            .select('*', { count: 'exact', head: true })
            .eq('owner_id', id)
            .eq('owner_type', 'Id::PromoEntity::Expert');
          const val = !error ? (count ?? 0) : 0;
          mediaCacheRef.current.set(id, val);
          out[id] = val;
        })
      );
    }

    return out;
  }, []);

  const backfillMetadata = useCallback(async (experts) => {
    const ids = experts.map(e => Number(e.id)).filter(Boolean);
    if (!ids.length) return experts;

    try {
      const [tags, mediaCounts] = await Promise.all([
        fetchTagsForIds(ids),
        fetchMediaCounts(ids),
      ]);

      return experts.map(e => ({
        ...e,
        tags: tags[e.id] ?? e.tags ?? [],
        mediaCount: mediaCounts[e.id] ?? e.mediaCount ?? 0,
      }));
    } catch {
      return experts;
    }
  }, [fetchTagsForIds, fetchMediaCounts]);

  const fetchExpertsFromSimilarity = useCallback(async (similarityIds, vipIds = []) => {
    const nonVipIds = similarityIds.filter(id => !vipIds.includes(id));

    const { data: experts } = nonVipIds.length
      ? await supabase.from('experts').select('*').in('id', nonVipIds)
      : { data: [] };

    const enriched = (experts || []).map(e => normalizeExpert(e, { isVip: false }));
    const shuffled = enriched.length > 1
      ? shuffleWithSeed(enriched, Math.min(150, enriched.length))
      : enriched.slice();

    return shuffled;
  }, []);

  return {
    fetchExpertsByIdsInOrder,
    fetchPriorityExperts,
    fetchTagsForIds,
    fetchMediaCounts,
    backfillMetadata,
    fetchExpertsFromSimilarity,
  };
}
